import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button.js';
import { Confetti } from './Confetti.js';
import './Celebration.css';

/**
 * CelebrationVariant — picks the hero icon (and a subtle accent class on the
 * trophy chip). Pass the right one for the moment:
 *   - `default`  trophy  — generic finish
 *   - `perfect`  star    — every exercise correct on the first try
 *   - `fast`     lightning bolt — elapsed time well under expected
 *   - `first`    party-popper — the user's first-ever completed lesson
 *   - `streak`   flame   — crossed a streak milestone (3/7/14/30/…)
 */
export type CelebrationVariant = 'default' | 'perfect' | 'fast' | 'first' | 'streak';

export interface CelebrationProps {
  /** When true, the overlay mounts and fires the confetti burst. */
  open: boolean;
  /** Visual flavor — picks the hero icon + accent. Default: 'default'. */
  variant?: CelebrationVariant;
  /** Title displayed above the stats. Default: "დასრულდა!" */
  title?: ReactNode;
  /** One-line subtitle under the title. */
  subtitle?: ReactNode;
  /** Stats shown as a row of pill cards (label + value). Pass 2-4. */
  stats?: Array<{ label: ReactNode; value: ReactNode }>;
  /** Primary CTA label. Default: "გააგრძელე". */
  ctaLabel?: ReactNode;
  onContinue: () => void;
}

/**
 * Celebration — full-screen "lesson complete!" overlay.
 * Green gradient bg, trophy pop-in, stat pills, primary CTA.
 * Fires a confetti burst every time `open` flips from false → true.
 */
export function Celebration({
  open,
  variant = 'default',
  title = 'დასრულდა!',
  subtitle = 'საუცხოოა! გააგრძელე.',
  stats,
  ctaLabel = 'გააგრძელე',
  onContinue,
}: CelebrationProps) {
  // Each open-flip becomes a new confetti trigger value.
  const [burst, setBurst] = useState(0);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (!wasOpen.current && open) setBurst((b) => b + 1);
    wasOpen.current = open;
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fluent-celebration"
      data-variant={variant === 'default' ? undefined : variant}
      role="dialog"
      aria-live="polite"
    >
      <Confetti trigger={burst} count={120} originY={0.25} />

      <span className="fluent-celebration__trophy" aria-hidden="true">
        <VariantIcon variant={variant} />
      </span>

      <h2 className="fluent-celebration__title">{title}</h2>
      <p className="fluent-celebration__subtitle">{subtitle}</p>

      {stats && stats.length > 0 && (
        <div className="fluent-celebration__stats">
          {stats.map((s, i) => (
            <div key={i} className="fluent-celebration__stat">
              <span className="fluent-celebration__stat-label">{s.label}</span>
              <span className="fluent-celebration__stat-value">
                <StatValue value={s.value} />
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="fluent-celebration__cta">
        <Button variant="neutral" size="lg" fullWidth onClick={onContinue}>
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}

/* ---------- Stat count-up ----------
 * Numeric stat values tick up from 0 → target, starting roughly when the
 * stat pills finish landing (CSS stagger ends ~400ms after mount).
 * Non-numeric values ("100%", "2:15", …) render static.
 */
const COUNT_UP_DELAY_MS = 400;
const COUNT_UP_DURATION_MS = 900;

function parseCountTarget(value: ReactNode): { prefix: string; target: number } | null {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return { prefix: '', target: value };
  }
  if (typeof value === 'string') {
    const match = /^(\+?)(\d+)$/.exec(value.trim());
    if (match) return { prefix: match[1] ?? '', target: Number(match[2]) };
  }
  return null;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function StatValue({ value }: { value: ReactNode }) {
  const parsed = parseCountTarget(value);
  if (!parsed) return <>{value}</>;
  return <CountUp prefix={parsed.prefix} target={parsed.target} />;
}

function CountUp({ prefix, target }: { prefix: string; target: number }) {
  const [shown, setShown] = useState(() => (prefersReducedMotion() ? target : 0));
  // Animate ONCE per mount, from 0 to the target present at mount time.
  // Some callers (LessonComplete) already animate the value THEY pass — a
  // per-change restart would cancel/reset forever and never display anything.
  // Later target changes snap straight to the new value instead.
  const animatedTarget = useRef<number | null>(null);
  const latestTarget = useRef(target);
  latestTarget.current = target;

  useEffect(() => {
    if (animatedTarget.current !== null) {
      // Already animated (or animating) — external ticks just snap.
      setShown(target);
      return;
    }
    animatedTarget.current = target;
    if (prefersReducedMotion()) {
      setShown(target);
      return;
    }
    let raf = 0;
    let startAt: number | null = null;
    const tick = (now: number) => {
      if (startAt === null) startAt = now + COUNT_UP_DELAY_MS;
      const t = (now - startAt) / COUNT_UP_DURATION_MS;
      if (t < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const clamped = Math.min(1, t);
      const eased = 1 - Math.pow(1 - clamped, 3); // easeOutCubic
      // Track the freshest target so a mid-flight prop change still lands right.
      setShown(Math.round(eased * latestTarget.current));
      if (clamped < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return (
    <>
      {prefix}
      {shown}
    </>
  );
}

function VariantIcon({ variant }: { variant: CelebrationVariant }) {
  switch (variant) {
    case 'perfect':   return <StarIcon />;
    case 'fast':      return <BoltIcon />;
    case 'first':     return <PartyIcon />;
    case 'streak':    return <FlameIcon />;
    case 'default':
    default:          return <TrophyIcon />;
  }
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M7 4h10v3h3a2 2 0 0 1-2 4h-1.6c-.6 2-2 3.6-4 4.3V19h3a1 1 0 1 1 0 2H8a1 1 0 1 1 0-2h3v-3.7c-2-.7-3.4-2.3-4-4.3H5.4a2 2 0 0 1-2-4h3zM5 6v2a1 1 0 0 0 .4.8l.2.1L6 9V6H5zm13 0v3l.4-.1.2-.1A1 1 0 0 0 19 8V6h-1z" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M12 2.5l2.9 6.3 6.9.8-5.1 4.8 1.3 6.9L12 18l-6 3.3 1.3-6.9L2.2 9.6l6.9-.8L12 2.5z" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M13.5 2L4 13.5h6L9 22l10.5-12h-6L13.5 2z" />
    </svg>
  );
}

function PartyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M5.5 20.5l4.5-13 7 7-13 6c-.4.2-.7-.1-.5-.6zM12.7 5.5l5.4 5.4 1.7-1.7c1-1 1-2.7 0-3.7s-2.7-1-3.7 0l-1.7 1.7-1.7 1.7zM18 14a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM14 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM21 7a1 1 0 1 1 2 0 1 1 0 0 1-2 0z" />
    </svg>
  );
}

function FlameIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M12 2.5c1.5 3 4.5 4.5 4.5 8.5 0 1.2-.4 2.3-1 3.2.7-.5 1.3-1.2 1.7-2 .9 1.4 1.3 2.9 1.3 4.3 0 3.3-2.9 6-6.5 6S5.5 19.8 5.5 16.5c0-2 1-3.7 2-5 .3.6.7 1.1 1.2 1.4-.4-.7-.7-1.5-.7-2.4 0-3 2-5.3 4-8z" />
    </svg>
  );
}
